<script setup lang="ts">
import { computed } from 'vue';

const props = withDefaults(defineProps<{
	id?: string;
	modelValue?: string;
	label?: string;
	placeholder?: string;
	isRequired?: boolean;
	isRequiredAsteriskVisible?: boolean;
	isInteractive?: boolean;
	areErrorsShown?: boolean;
	validationErrors?: string[];
	theme: string;
	tag?: 'input' | 'textarea';
	isTextareaResizable?: boolean;
}>(), {
	modelValue: '',
	isRequired: false,
	isRequiredAsteriskVisible: true,
	isInteractive: true,
	areErrorsShown: false,
	validationErrors: () => [],
	theme: 'light',
	tag: 'input',
	isTextareaResizable: true,
});

const emit = defineEmits<{
	'update:model-value': [value: string],
}>();

const computedClasses = computed(() => [
	{
		'input__component--error': props.validationErrors.length && props.areErrorsShown,
	},
	{
		'input__component--textarea': props.tag === 'textarea',
	},
	{
		'input__component--read-only': !props.isInteractive,
	},
	`input__component--${props.theme}`,
	{
		'input__component--no-textarea-resize': props.tag === 'textarea' && !props.isTextareaResizable,
	},
]);
</script>

<template>
	<div
		class="input"
		:class="`input--${theme}`"
	>
		<label
			:for="id"
			class="input__label"
			:class="`input__label--${theme}`"
		>
			{{ label }}{{ isRequired && isRequiredAsteriskVisible ? '*' : '' }}
		</label>
		<input
			v-if="tag === 'input'"
			:id="id"
			:placeholder="placeholder"
			type="text"
			class="input__component"
			:class="computedClasses"
			:value="modelValue"
			:readonly="!isInteractive"
			:tabindex="isInteractive ? 0 : -1"
			@input="emit('update:model-value', ($event.target as HTMLInputElement).value)"
			@click.prevent
			@dragstart.prevent
			@drag.prevent
		>
		<textarea
			v-if="tag === 'textarea'"
			:id="id"
			:placeholder="placeholder"
			type="text"
			class="input__component"
			:class="computedClasses"
			:value="modelValue"
			:readonly="!isInteractive"
			:tabindex="isInteractive ? 0 : -1"
			@input="emit('update:model-value', ($event.target as HTMLInputElement).value)"
			@click.prevent
			@dragstart.prevent
			@drag.prevent
		/>
		<div v-if="validationErrors.length && areErrorsShown">
			<p
				v-for="message in validationErrors"
				:key="message"
				class="input__error-message text-body-2"
			>
				{{ message }}
			</p>
		</div>
	</div>
</template>

<style lang="scss" scoped>
/* stylelint-disable custom-property-pattern */
@import "@zyro-inc/site-modules/scss/mixins/site-engine-mobile";

.input {
	font-size: var(--input-text-size, var(--body-font-size));

	$input-transition-timing-function: "ease";
	$input-transition-duration: "0.2s";

	&__component {
		width: 100%;
		height: var(--input-height);
		padding: 16px;
		font-family: var(--form-font-family, var(--body-font-family));
		font-size: var(--input-text-size, var(--body-font-size));
		font-weight: var(--form-font-weight, var(--body-font-weight));
		color: var(--input-text-color, var(--body-color));
		background-color: var(--input-fill-color, $color-gray-border);
		border-color: var(--input-border-color, $color-dark);
		border-style: solid;
		border-width: var(--input-border-width, 0);
		border-radius: var(--input-border-radius, 0);
		outline: none;
		transition:
			color #{$input-transition-timing-function} #{$input-transition-duration},
			border-color #{$input-transition-timing-function} #{$input-transition-duration},
			background-color #{$input-transition-timing-function} #{$input-transition-duration};

		&::placeholder {
			font-family: var(--form-font-family, var(--body-font-family));
			font-size: var(--input-text-size, 14px);
			color: var(--input-text-color, $color-gray);
			opacity: 0.5;
			transition: color #{$input-transition-timing-function} #{$input-transition-duration};
		}

		&:hover,
		&:focus {
			color: var(--input-text-color--hover, var(--body-color));
			background-color: var(--input-fill-color--hover, $color-gray-border);
			border-color: var(--input-border-color--hover, $color-dark);

			&::placeholder {
				color: var(--input-text-color--hover, $color-gray);
			}
		}

		&--dark {
			background-color: var(--input-fill-color, rgba($color-gray-light, 0.6));

			&::placeholder {
				color: var(--input-text-color, $color-gray-dark);
			}

			&:hover,
			&:focus {
				background-color: var(--input-fill-color--hover, rgba($color-gray-light, 0.6));

				&::placeholder {
					color: var(--input-text-color--hover, $color-gray-dark);
				}
			}
		}

		&--read-only {
			cursor: move;
		}

		&--textarea {
			min-height: 96px;
			overflow: auto;
			resize: vertical;
		}

		&--error {
			border-color: var(--color-danger-dark);
			border-width: 1px;

			&:active, &:focus, &:hover {
				border-color: var(--color-danger-dark);
			}
		}

		&--no-textarea-resize {
			resize: none;
			overflow: auto;
		}
	}

	&__label {
		font-size: var(--label-text-size, var(--body-font-size));
		color: var(--label-text-color, $color-dark);

		&--dark {
			color: var(--label-text-color, $color-gray-border);
		}
	}

	&__error-message {
		color: var(--color-danger-dark);
	}

	&--is-invalid {
		.input__component {
			border-color: $color-danger;
		}
	}
}

@include site-engine-mobile {
	.input {
		&__component {
			font-size: var(--m-input-text-size, var(--body-m-font-size));

			&::placeholder {
				font-size: var(--m-input-text-size, 14px);
			}
		}

		&__label {
			font-size: var(--m-label-text-size, var(--body-m-font-size));
		}
	}
}

</style>
